import { apiFetch } from "./apiFetch";
import { getApiErrorMessage } from "./apiError";

const API_URL = process.env.REACT_APP_API_URL;

export type SettlementType = "Charge" | "Deduction";

export type SettlementLine = {
  systemId: string;
  lineNo: number | null;
  description: string | null;
  quantity: number | null;
  directUnitCost: number | null;
  amount: number | null;
  settlementType: "None" | SettlementType;
  skipInPosting: boolean;
  skipInSettlement: boolean;
  settlementAmountEditable: boolean;
  skipInPostingAllowed: boolean;
  lineEditable: boolean;
  lineMatched: boolean;
};

export type SettlementFeatures = {
  useKSeFSettlementHandling: boolean;
  allowSettlementAmountEdit: boolean;
  allowManualSettlementLine: boolean;
  allowSettlementLineSkip: boolean;
  requireSettlementDimensions: boolean;
};

function url(companyId: string, suffix: string) {
  return `${API_URL}/documents/${suffix}?${new URLSearchParams({ companyId })}`;
}

async function json<T>(endpoint: string): Promise<T> {
  const res = await apiFetch(endpoint);
  if (!res.ok) throw new Error(await getApiErrorMessage(res, "Błąd pobierania danych rozliczeń KSeF."));
  return res.json();
}

async function send(endpoint: string, method: "PATCH" | "POST", body: object): Promise<void> {
  const res = await apiFetch(endpoint, {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await getApiErrorMessage(res, "Nie udało się zapisać rozliczenia KSeF."));
}

export const getSettlementFeatures = (companyId: string) =>
  json<SettlementFeatures>(url(companyId, "purchase-line-features"));

export async function getSettlementLines(companyId: string, documentId: string) {
  const response = await json<{ items: SettlementLine[] }>(url(
    companyId, `${encodeURIComponent(documentId)}/purchase-lines`));
  return Array.isArray(response.items) ? response.items : [];
}

export async function updateSettlementAmount(
  companyId: string, documentId: string, lineId: string, amount: number,
) {
  await send(url(companyId,
    `${encodeURIComponent(documentId)}/purchase-lines/${encodeURIComponent(lineId)}/settlement-amount`),
    "PATCH", { directUnitCost: amount });
}

export async function updateSettlementFlags(
  companyId: string, documentId: string, lineId: string,
  flags: { skipInPosting?: boolean; skipInSettlement?: boolean },
) {
  await send(url(companyId,
    `${encodeURIComponent(documentId)}/purchase-lines/${encodeURIComponent(lineId)}/settlement-flags`),
    "PATCH", flags);
}

export async function createSettlementLine(
  companyId: string, documentId: string, kind: SettlementType, amount: number,
) {
  await send(url(companyId, `${encodeURIComponent(documentId)}/settlement-lines`),
    "POST", { settlementType: kind, amount });
}
